import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, firstValueFrom, map, retry, timeout, timer, TimeoutError } from 'rxjs';
import SparkMD5 from 'spark-md5';
import { GlobalsService } from './globals.service';
import { UserStateService } from './user-state.service';
import { ApiService } from './api.service';
import { PathService } from './path.service';
import { ProjectInfo, ShareInfo } from '../models';

/**
 * Parameters for uploading project bytes to the server
 */
export interface ProjectUploadParams {
  serverName: string;
  projectId: number;
  productId: number;
  productName: string;
  productTypeId: number;
  name?: string;
  thumb: string;
  bytes: string;
  fileSize: number;
  md5: string;
  themeSetId?: number;
  themeColorId?: number;
  photosUploaded?: number;
}

/**
 * Project Service - converted from ProjectDelegate.as
 *
 * AMFphp Service: ProjectService
 * Methods:
 * - getProjects(editorTypeId, productId, user)
 * - insert(projectInfo, user) - Create new project record
 * - deleteProject(project, user)
 * - cloneProject(project, user)
 * - setProjectPhotos(projectId, photos, user)
 * - checkProjectPhotos(photos, user)
 * - rename(project, user)
 *
 * EditorService methods (also in ProjectDelegate):
 * - getProduct(request)
 * - getProductFormat(productId, credentials)
 * - getProductFinish(productId, finishId, locale, credentials)
 *
 * HTTP Endpoints:
 * - POST /UploadProjectBytes.php - Upload project bytes after insert
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(GlobalsService);
  private readonly userState = inject(UserStateService);
  private readonly api = inject(ApiService);
  private readonly pathService = inject(PathService);

  /**
   * Get projects
   * AMF: ProjectService.getProjects(editorTypeId, productId, user)
   */
  getProjects(editorTypeId: number = 0, productId: number = 0): Observable<ProjectInfo[]> {
    const params: Record<string, any> = {};
    if (editorTypeId) params['editorTypeId'] = editorTypeId;
    if (productId) params['productId'] = productId;
    return this.api.get<any>('/projects', { params }).pipe(
      map(res => {
        const items: any[] = res?.items || res || [];
        // Map API field names to frontend model field names
        return items.map(p => {
          // Use thumbnailUrl from API, but never use .dbproject URLs as thumbnails
          let thumb = p.thumbnailUrl || p.thumbnail || '';
          if (thumb.includes('.dbproject')) thumb = '';
          return {
            ...p,
            lastModifiedDateString: p.lastModifiedDateString || p.lastModificationDate || '',
            creationDateString: p.creationDateString || p.creationDate || '',
            thumbnail: thumb,
          };
        });
      }),
      catchError(() => of([]))
    );
  }

  getProjectsBin(): Observable<ProjectInfo[]> {
    // Get deleted/archived projects - same as getProjects with different filter
    return this.getProjects(0, 0);
  }

  getProjectDetails(projectId: number): Observable<ProjectInfo | null> {
    // Use getProjects and filter, or implement specific call if available
    return of(null);
  }

  createProject(name: string, productId: number): Observable<ProjectInfo | null> {
    // Project creation is typically done through EditorService
    return of(null);
  }

  updateProject(projectId: number, data: Partial<ProjectInfo>): Observable<ProjectInfo | null> {
    // Use rename for name updates
    return of(null);
  }

  /**
   * Delete project
   * AMF: ProjectService.deleteProject(project, user)
   * The REST endpoint requires the owning customer id (400 "Customer ID is
   * required" otherwise) — /projects returns customerId on each project; fall
   * back to the logged-in user. Errors are NOT swallowed anymore: the old
   * catchError(of(false)) made callers report "deleted" while the backend had
   * refused, so the project reappeared on refresh.
   */
  deleteProject(project: ProjectInfo): Observable<any> {
    const customerId = Number((project as any).customerId) || Number(this.userState.user()?.id) || 0;
    return this.api.delete('/projects/' + project.id, customerId > 0 ? { params: { customerId } } : undefined).pipe(
      map((res: any) => {
        if (res && res.success === false) {
          throw new Error(res.error || 'Project delete failed');
        }
        return res;
      })
    );
  }

  restoreProject(projectId: number): Observable<boolean> {
    // Would need specific endpoint - not in original delegate
    return of(false);
  }

  /**
   * Rename project
   * AMF: ProjectService.rename(project, user)
   * Mirrors the drafts rename endpoint shape (PATCH /drafts/{id}/rename) and sends
   * customerId like deleteProject — the REST project endpoints require the owner id.
   * Errors propagate so callers can react truthfully.
   */
  renameProject(project: ProjectInfo, newName: string): Observable<any> {
    const customerId = Number((project as any).customerId) || Number(this.userState.user()?.id) || 0;
    return this.api.patch('/projects/' + project.id + '/rename', {
      name: newName,
      ...(customerId > 0 ? { customerId } : {})
    }).pipe(
      map((res: any) => {
        if (res && res.success === false) {
          throw new Error(res.error || 'Project rename failed');
        }
        return res;
      })
    );
  }

  /**
   * Fetch a single project's metadata (authoritative serverId / serverName /
   * pathStructureId + backend url / thumbnailUrl). Used when restoring a draft
   * cold so the cover preview thumbnail resolves against the project's CURRENT
   * server instead of a possibly-stale serverId persisted in the draft config.
   * Returns null on error so callers can fall back to the persisted values.
   */
  getProjectMeta(projectId: number): Observable<any | null> {
    return this.api.get<any>('/projects/' + projectId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Clone/duplicate project
   * AMF: ProjectService.cloneProject(project, user)
   */
  duplicateProject(project: ProjectInfo): Observable<any> {
    return this.api.post('/projects/' + project.id + '/clone').pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Set project photos
   * AMF: ProjectService.setProjectPhotos(projectId, photos, user)
   */
  setProjectPhotos(projectId: number, photos: any[]): Observable<any> {
    return this.api.post('/projects/' + projectId + '/photos', { photos }).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Check project photos
   * AMF: ProjectService.checkProjectPhotos(photos, user)
   */
  checkProjectPhotos(photos: any[]): Observable<any> {
    return this.api.post('/projects/photos/check', { photos }).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Upload project bytes to the server
   * POST multipart form-data to /upload/projects-bytes
   * @param params - Upload parameters including project data and thumbnail
   * @returns Promise<boolean> - true on success
   */
  async uploadProjectBytes(params: ProjectUploadParams): Promise<any> {
    console.log('[ProjectService] uploadProjectBytes:', {
      projectId: params.projectId,
      serverName: params.serverName,
      fileSize: params.fileSize,
      md5: params.md5
    });

    try {
      // Symfony controller reads $request->request->all() (form-data), not JSON body
      const formData = new FormData();
      formData.append('bytes', params.bytes);
      formData.append('md5', params.md5);
      formData.append('serverName', params.serverName);
      formData.append('projectId', params.projectId.toString());
      formData.append('productId', params.productId.toString());
      formData.append('productName', params.productName || '');
      formData.append('productTypeId', (params.productTypeId || 0).toString());
      formData.append('fileSize', params.fileSize.toString());
      if (params.name) formData.append('name', params.name);
      if (params.thumb) formData.append('thumb', params.thumb);
      if (params.themeSetId) formData.append('themeSetId', params.themeSetId.toString());
      if (params.themeColorId) formData.append('themeColorId', params.themeColorId.toString());
      if (params.photosUploaded != null) formData.append('photosUploaded', params.photosUploaded.toString());

      const path = params.projectId > 0
        ? `/upload/projects-bytes/${params.projectId}`
        : '/upload/projects-bytes';
      // Timeout + bounded retry: a hung connection must not leave the save
      // pending forever, and transient network/5xx failures get two retries
      // (safe — the upload is idempotent: same bytes, same md5, same key).
      // 4xx errors don't retry (auth/refresh is handled inside ApiService).
      const response = await firstValueFrom(
        this.api.upload<any>(path, formData).pipe(
          timeout(120_000),
          retry({
            count: 2,
            delay: (error, retryCount) => {
              const status = Number(error?.status);
              const retriable = error instanceof TimeoutError || !status || status === 0 || status >= 500;
              if (!retriable) throw error;
              console.warn(`[ProjectService] uploadProjectBytes retry ${retryCount} after`, error?.message || error);
              return timer(2000 * retryCount);
            }
          })
        )
      );

      console.log('[ProjectService] uploadProjectBytes response:', response);
      // Symfony returns project object on success (201), or progress on chunk
      return response ?? null;
    } catch (error) {
      console.error('[ProjectService] uploadProjectBytes error:', error);
      return null;
    }
  }

  /**
   * Generate a UUID for new projects
   * Format: XXXXXXXX-XXXX-4XXX-YXXX-XXXXXXXXXXXX (uppercase)
   */
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16).toUpperCase();
    });
  }

  /**
   * Compute MD5 hash of a string (for project JSON)
   * Uses SparkMD5 library (same as upload.service.ts)
   */
  computeStringMD5(data: string): string {
    const spark = new SparkMD5();
    spark.append(data);
    return spark.end();
  }

  // EditorService methods

  /**
   * Get product for editor
   * AMF: EditorService.getProduct(request)
   */
  getProduct(request: any): Observable<any> {
    return this.api.get(`/editor/products/${request.productId}`, {
      params: {
        editorTypeId: request.editorTypeId,
        finishId: request.finishId ?? 0
      }
    }).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get product format
   * AMF: EditorService.getProductFormat(productId, credentials)
   */
  getProductFormat(productId: number): Observable<any> {
    return this.api.get('/editor/formats/' + productId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get product finish
   * AMF: EditorService.getProductFinish(productId, finishId, locale, credentials)
   */
  getProductFinish(productId: number, finishId: number): Observable<any> {
    return this.api.get('/editor/products/' + productId + '/finishes/' + finishId).pipe(
      catchError(() => of(null))
    );
  }

  // Share related methods - these are from ShareService/ShareDelegate
  getShares(): Observable<ShareInfo[]> {
    // ShareService is a JSON HTTP service, not AMF
    const user = this.userState.user();
    if (!user) return of([]);

    return this.http.get<ShareInfo[]>(`${this.globals.shareUrl}api/shares`, {
      params: { userId: user.id.toString() }
    }).pipe(
      catchError(() => of([]))
    );
  }

  createShare(projectId: number, email: string, permissions: number): Observable<ShareInfo | null> {
    const user = this.userState.user();
    if (!user) return of(null);

    return this.http.post<ShareInfo>(`${this.globals.shareUrl}api/shares`, {
      userId: user.id,
      projectId,
      email,
      permissions
    }).pipe(
      catchError(() => of(null))
    );
  }

  updateSharePermissions(shareId: number, permissions: number): Observable<ShareInfo | null> {
    const user = this.userState.user();
    if (!user) return of(null);

    return this.http.put<ShareInfo>(`${this.globals.shareUrl}api/shares/${shareId}/permissions`, {
      userId: user.id,
      permissions
    }).pipe(
      catchError(() => of(null))
    );
  }

  deleteShare(shareId: number): Observable<boolean> {
    const user = this.userState.user();
    if (!user) return of(false);

    return this.http.delete<boolean>(`${this.globals.shareUrl}api/shares/${shareId}`, {
      params: { userId: user.id.toString() }
    }).pipe(
      catchError(() => of(false))
    );
  }
}
